import { inject, Injectable } from '@angular/core';
import { Regimen, User } from '@cureskin/api-client/src/server-api';
import { ExperimentService } from '@services/experiment';
import { TimeService } from '@services/time-service';
import { UserService } from '@services/user/user.service';
import { extractRegimenProducts } from 'client/helper/regimen-helper';

@Injectable({ providedIn: 'root' })
export class ShopService {
  private readonly experimentService = inject(ExperimentService);
  private readonly timeService = inject(TimeService);
  private readonly userService = inject(UserService);

  getRegimenProducts(regimens: Regimen[]): any[] {
    const unlocked: any[] = [];
    const locked: any[] = [];

    for (const regimen of regimens) {
      const regimenProducts = extractRegimenProducts(regimen);
      const hasRepairProduct = regimenProducts.some(item => !!item.product?.isRepair);

      regimenProducts.forEach((item, index) => {
        const isLocked = hasRepairProduct
          ? !!item.product?.isRepair
          : index === regimenProducts.length - 1;
        const product = { ...item.product, isLocked };

        if (isLocked) {
          locked.push(product);
        } else {
          unlocked.push(product);
        }
      });
    }

    return [...unlocked, ...locked];
  }

  shouldShowAcqShopBanner(user: User, regimens: Regimen[]): boolean {
    if (!user || !this.userService.isNewOrUnpaidUser(user)) return false;
    if (!regimens?.length) return false;

    const { startDay, endDay } = this.experimentService.getAcqShopExperiment()?.variant ?? {};
    if (startDay == null || endDay == null) return false;

    const oldestRegimen = this.getOldestRegimen(regimens);
    if (!oldestRegimen) return false;

    const daysSinceCreation = this.timeService.differenceInDays(new Date(), new Date(oldestRegimen.createdAt));
    return daysSinceCreation >= startDay && daysSinceCreation <= endDay;
  }

  getRegimenPricing(regimenClass: string, regimens: Regimen[]): { fixedPrice: number; fixedPriceMRP: number; discount: number } {
    const regimen = regimens.find(r => r?.class === regimenClass) ?? regimens[0];
    const fixedPrice = regimen?.fixedPrice ?? 0;
    const fixedPriceMRP = regimen?.fixedPriceMRP ?? 0;
    const discount = this.getDiscountPercent(regimen);
    return { fixedPrice, fixedPriceMRP, discount };
  }

  getDiscountPercent(regimen: Regimen | null | undefined): number {
    if (!regimen?.fixedPriceMRP || !regimen?.fixedPrice) return 0;
    return Math.floor(((regimen.fixedPriceMRP - regimen.fixedPrice) * 100) / regimen.fixedPriceMRP);
  }

  private getOldestRegimen(regimens: Regimen[]): Regimen | undefined {
    return regimens.reduce<Regimen | undefined>((oldest, current) => {
      if (!oldest) return current;
      return new Date(current.createdAt) < new Date(oldest.createdAt) ? current : oldest;
    }, undefined);
  }
}
